/**
 * BrokerageCard — one brokerage tile in the overview grid.
 *
 * Shows an initials avatar, the firm name + contact person, a tier badge,
 * a RERA-expiry warning when the licence lapses within 60 days, and a
 * stats footer (total leads / won / conversion %). Clicking opens the
 * brokerage detail screen.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, User } from 'lucide-react';

// Tier → badge styling. Top performers get the strongest colour.
export const TIER_STYLES = {
  Top: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  Mid: 'bg-blue-100 text-blue-700 border-blue-300',
  Low: 'bg-slate-100 text-slate-600 border-slate-300',
};

// Deterministic avatar colour so the same firm always looks the same.
const AVATAR_TONES = [
  'bg-violet-500', 'bg-blue-500', 'bg-emerald-500',
  'bg-amber-500', 'bg-rose-500', 'bg-cyan-500',
];

const initialsOf = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';

const toneFor = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
};

/**
 * Days until a RERA licence expires. Returns null when no expiry is set.
 * Negative means already expired.
 */
// Compact "12 Mar 2026, 02:30 PM" formatter for the created-on stamp.
export const fmtDateTime = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString([], {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export const reraExpiryDays = (rera_expiry) => {
  if (!rera_expiry) return null;
  const d = new Date(rera_expiry);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

export default function BrokerageCard({ brokerage, onClick }) {
  const stats = brokerage.stats || {};
  const tier = brokerage.tier || 'Mid';
  const expiryDays = reraExpiryDays(brokerage.rera_expiry);
  const expirySoon = expiryDays !== null && expiryDays <= 60;
  const isInactive = (brokerage.status || 'Active').toLowerCase() === 'inactive';

  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md hover:border-violet-300 ${
        isInactive ? 'opacity-60' : ''
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header — avatar + name + tier */}
        <div className="flex items-start gap-3">
          <div
            className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center text-white font-semibold ${toneFor(
              brokerage.id,
            )}`}
          >
            {initialsOf(brokerage.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                {brokerage.name || 'Unnamed brokerage'}
              </h3>
              {isInactive && (
                <Badge variant="outline" className="text-[10px] text-slate-400">
                  Inactive
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
              <User size={11} />
              {brokerage.contact_person || 'No contact person'}
            </p>
            {/* Ownership row — only render if at least one slot is
                filled so empty cards stay compact. */}
            {(brokerage.bdm_name || brokerage.sales_manager_name) && (
              <p className="mt-0.5 truncate text-[11px] text-slate-400">
                {brokerage.bdm_name && <>BDM: <span className="text-slate-600">{brokerage.bdm_name}</span></>}
                {brokerage.bdm_name && brokerage.sales_manager_name && ' · '}
                {brokerage.sales_manager_name && <>SM: <span className="text-slate-600">{brokerage.sales_manager_name}</span></>}
              </p>
            )}
          </div>
          <Badge className={`text-xs border ${TIER_STYLES[tier] || TIER_STYLES.Mid}`}>
            {tier}
          </Badge>
        </div>

        {/* RERA expiry warning */}
        {expirySoon && (
          <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            <AlertTriangle size={12} />
            {expiryDays < 0
              ? 'RERA licence expired'
              : `RERA licence expires in ${expiryDays} day${expiryDays === 1 ? '' : 's'}`}
          </div>
        )}

        {/* Stats footer */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <Stat label="Leads" value={stats.total_leads ?? 0} />
          <Stat label="Won" value={stats.won_leads ?? 0} tone="text-emerald-600" />
          <Stat
            label="Conversion"
            value={`${stats.conversion_rate ?? 0}%`}
            tone="text-violet-600"
          />
        </div>

        {/* When this brokerage was added. */}
        {fmtDateTime(brokerage.created_at) && (
          <p className="text-[10px] text-slate-400 text-center">
            Added {fmtDateTime(brokerage.created_at)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="text-center">
    <div className={`text-lg font-bold ${tone || 'text-slate-900 dark:text-slate-100'}`}>
      {value}
    </div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);
